import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import type { ConnectAgent } from '@/types';
import {
  checkIsAfterCallWorkState,
  checkIsErrorState,
} from '@/constants/amazonConnect';
import { useAmazonConnectReady } from './AmazonConnectCCP';

function safeCleanup(cleanup: (() => void) | undefined) {
  if (cleanup) {
    try {
      cleanup();
    } catch {
      // ignore
    }
  }
}

export interface AmazonConnectAgentContextValue {
  agentState: string;
  isRoutable: boolean;
  agentName: string;
  isAfterCallWork: boolean;
  isErrorState: boolean;
  isChangingState: boolean;
  setIsChangingState: (value: boolean) => void;
  agent: ConnectAgent | null;
  muted: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AmazonConnectAgentContext =
  createContext<AmazonConnectAgentContextValue | null>(null);

interface AmazonConnectAgentProviderProps {
  children: ReactNode;
}

/**
 * Single subscription to the Connect agent; all consumers share the same state
 * so the header and panel stay in sync when agent state changes.
 */
export function AmazonConnectAgentProvider({
  children,
}: AmazonConnectAgentProviderProps) {
  const { isReady } = useAmazonConnectReady();
  const [agentState, setAgentState] = useState<string>('Unknown');
  const [isRoutable, setIsRoutable] = useState(false);
  const [agentName, setAgentName] = useState<string>('');
  const [isAfterCallWork, setIsAfterCallWork] = useState(false);
  const [isErrorState, setIsErrorState] = useState(false);
  const [isChangingState, setIsChangingState] = useState(false);
  const [agent, setAgent] = useState<ConnectAgent | null>(null);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    if (!isReady) return;
    let unsubscribeMute: (() => void) | undefined;
    let unsubscribeStateChange: (() => void) | undefined;

    const updateAgentState = (agentObj: ConnectAgent) => {
      try {
        const currentState = agentObj.getState();
        const ROUTABLE = connectAdapter.getCcpEnums().AgentStateType.ROUTABLE;
        const stateName = currentState?.name || 'Unknown';
        console.log(
          '[AC-Agent] updateAgentState',
          JSON.stringify({
            stateName,
            isRoutable: currentState?.type === ROUTABLE,
            isAfterCallWork: checkIsAfterCallWorkState(stateName),
            isErrorState: checkIsErrorState(stateName),
          })
        );
        setAgentState(stateName);
        setAgentName(agentObj.getName());
        setIsRoutable(currentState?.type === ROUTABLE);
        setIsAfterCallWork(checkIsAfterCallWorkState(stateName));
        setIsErrorState(checkIsErrorState(stateName));
      } catch {
        setIsChangingState(false);
      }
    };

    const updateStateFromChange = (agentStateChange: {
      agent?: ConnectAgent;
      newState: { type: unknown; name: string } | string;
    }) => {
      console.log(
        '[AC-Agent] onStateChange received',
        JSON.stringify({
          newStateType: typeof agentStateChange.newState,
          newStateValue:
            typeof agentStateChange.newState === 'string'
              ? agentStateChange.newState
              : agentStateChange.newState.name,
        })
      );
      const ROUTABLE = connectAdapter.getCcpEnums().AgentStateType.ROUTABLE;
      let newStateName: string;
      let isRoutableValue = false;

      if (typeof agentStateChange.newState === 'string') {
        newStateName = agentStateChange.newState;
        if (agentStateChange.agent) {
          try {
            const currentState = agentStateChange.agent.getState();
            if (currentState) {
              isRoutableValue = currentState.type === ROUTABLE;
              if (currentState.name) newStateName = currentState.name;
            }
          } catch {
            // ignore
          }
        }
      } else {
        newStateName = agentStateChange.newState.name || 'Unknown';
        isRoutableValue = agentStateChange.newState.type === ROUTABLE;
      }

      setAgentState(newStateName);
      setIsRoutable(isRoutableValue);
      setIsChangingState(false);
      setIsAfterCallWork(checkIsAfterCallWorkState(newStateName));
      setIsErrorState(checkIsErrorState(newStateName));
    };

    const unsubscribeAgent = connectAdapter.onAgent(agent => {
      try {
        const agentObj = agent as ConnectAgent;
        setAgent(agentObj);
        setMuted(Boolean((agentObj as { muted?: boolean }).muted));

        if (typeof agentObj.onMuteToggle === 'function') {
          unsubscribeMute = agentObj.onMuteToggle(
            (status: { muted: boolean }) => setMuted(status.muted)
          );
        }

        updateAgentState(agentObj);

        unsubscribeStateChange = agentObj.onStateChange(agentStateChange => {
          try {
            updateStateFromChange({
              ...agentStateChange,
              agent: agentObj,
            });
          } catch {
            setIsChangingState(false);
          }
        });

        agentObj.onRefresh(() => {
          try {
            updateAgentState(agentObj);
          } catch {
            // ignore
          }
        });
      } catch {
        setIsChangingState(false);
      }
    });

    return () => {
      safeCleanup(unsubscribeMute);
      safeCleanup(unsubscribeStateChange);
      safeCleanup(unsubscribeAgent);
    };
  }, [isReady]);

  const value: AmazonConnectAgentContextValue = {
    agentState,
    isRoutable,
    agentName,
    isAfterCallWork,
    isErrorState,
    isChangingState,
    setIsChangingState,
    agent,
    muted,
  };

  return (
    <AmazonConnectAgentContext.Provider value={value}>
      {children}
    </AmazonConnectAgentContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAmazonConnectAgentContext(): AmazonConnectAgentContextValue {
  const ctx = useContext(AmazonConnectAgentContext);
  if (!ctx) {
    throw new Error(
      'useAmazonConnectAgentContext must be used within AmazonConnectAgentProvider'
    );
  }
  return ctx;
}

const defaultAgentState = {
  agentState: 'Unknown',
  isRoutable: false,
  agentName: '',
  isAfterCallWork: false,
  isErrorState: false,
  isChangingState: false,
  setIsChangingState: (_: boolean) => {},
  agent: null as ConnectAgent | null,
  muted: false,
} satisfies AmazonConnectAgentContextValue;

/** Use agent state; returns defaults when outside AmazonConnectAgentProvider. */
// eslint-disable-next-line react-refresh/only-export-components
export function useAmazonConnectAgent(): AmazonConnectAgentContextValue {
  const ctx = useContext(AmazonConnectAgentContext);
  return ctx ?? defaultAgentState;
}
